/* eslint-disable no-inner-declarations */
/* eslint-disable @typescript-eslint/no-namespace */
import { IssueDate } from '../../../helpers/issue/date';
import moment from 'moment';
import { isDefined } from '../../../utils/utils';

const firstCallDatePriorityRange: string[] = [
  'date',
  'Anytime',
  'MWC',
  '-',
  'Various'
];

export namespace DealDateComparators {
  export function dealDateComparatorWithoutTime(
    valA: IssueDate.DealWithFormattedDate,
    valB: IssueDate.DealWithFormattedDate,
    _nodeA,
    _nodeB,
    isInverted: boolean
  ): number {
    const valWithDateOnlyA = {
      ...valA,
      dealDate: valA.dealDate && valA.dealDate.clone().startOf('day')
    };
    const valWithDateOnlyB = {
      ...valB,
      dealDate: valB.dealDate && valB.dealDate.clone().startOf('day')
    };
    return DealDateComparators.dealDateComparator(
      valWithDateOnlyA,
      valWithDateOnlyB,
      _nodeA,
      _nodeB,
      isInverted
    );
  }

  export function dealDateComparator(
    valA: IssueDate.DealWithFormattedDate,
    valB: IssueDate.DealWithFormattedDate,
    _nodeA,
    _nodeB,
    isInverted: boolean
  ): number {
    // Handle null or undefined values
    if (!isDefined(valA) || !isDefined(valB)) {
      return 0;
    }

    const priorityTypeA = getDealPriorityType(valA);
    const priorityTypeB = getDealPriorityType(valB);
    if (
      priorityTypeA === IssueDate.Priority.date &&
      priorityTypeB === IssueDate.Priority.date
    ) {
      return compareDealsDates(valA, valB);
    }
    if (priorityTypeA === IssueDate.Priority.date) {
      return isInverted ? 1 : -1;
    }
    if (priorityTypeB === IssueDate.Priority.date) {
      return isInverted ? -1 : 1;
    }
    return compareDealsPriorityTypes(priorityTypeA, priorityTypeB);
  }

  function getDealPriorityType({
    isTBD,
    isDayToDay
  }: IssueDate.DealWithFormattedDate): IssueDate.Priority {
    const isDayToDayCondition = isDayToDay
      ? IssueDate.Priority.DayToDay
      : IssueDate.Priority.date;

    return isTBD ? IssueDate.Priority.TBD : isDayToDayCondition;
  }

  function compareDealsDates(
    valA: IssueDate.DealWithFormattedDate,
    valB: IssueDate.DealWithFormattedDate
  ): number {
    if (valA.isWeekOf === valB.isWeekOf) {
      const dealDateIsSame = valA.dealDate.isSame(valB.dealDate) ? 0 : -1;

      return valA.dealDate.isAfter(valB.dealDate) ? 1 : dealDateIsSame;
    } else {
      return compareDifferentWeeks(valA, valB);
    }
  }

  function compareDifferentWeeks(valA, valB) {
    if (valA.isWeekOf && !valB.isWeekOf) {
      return valB.dealDate.isBefore(valA.dealDate) ? 1 : -1;
    } else {
      return valA.dealDate.isBefore(valB.dealDate) ? -1 : 1;
    }
  }

  export function compareDealsPriorityTypes(
    priorityTypeA: IssueDate.Priority,
    priorityTypeB: IssueDate.Priority
  ): number {
    const priorityA = getDealDatePriority(priorityTypeA);
    const priorityB = getDealDatePriority(priorityTypeB);
    return priorityA - priorityB;
  }

  function getDealDatePriority(type: IssueDate.Priority) {
    const priorityByType = {
      [IssueDate.Priority.date]: 0,
      [IssueDate.Priority.DayToDay]: 1,
      [IssueDate.Priority.TBD]: 2
    };

    return priorityByType[type];
  }
}

export function dealDateSetFilterComparator(valA, valB): number {
  const a = valA.weekOf ? valA.weekOf.format('d/M') : valA; //.isoWeekday();
  const b = valB.weekOf ? valB.weekOf.format('d/M') : valB;
  const aEqualB = a === b ? 0 : -1;

  return a > b ? 1 : aEqualB;
}

export function datedDateComparator(valA, valB): number {
  const valAEqualValB = valA.length === valB.length ? 0 : -1;

  if (valA.length !== valB.length) {
    return valA.length > valB.length ? 1 : valAEqualValB;
  }

  if (!valA[0]) {
    return -1;
  }
  if (!valB[0]) {
    return 1;
  }

  const dateFirst = moment(valA[0]);
  const dateSecond = moment(valB[0]);
  const dateIsSame = dateFirst.isSame(dateSecond) ? 0 : -1;

  return dateFirst.isAfter(dateSecond) ? 1 : dateIsSame;
}

export function firstCallDateComparator(
  valA,
  valB,
  _nodeA,
  _nodeB,
  isInverted
): number {
  const isEmptyString = (str) => {
    if (str && typeof str === 'string') {
      return str.trim() === '';
    }
    return false;
  };
  const valAEmpty = !valA || valA.length === 0 || valA.every(isEmptyString);
  const valBEmpty = !valB || valB.length === 0 || valB.every(isEmptyString);

  if (valAEmpty && valBEmpty) {
    return 0;
  }
  if (valAEmpty) {
    return -1;
  } else if (valBEmpty) {
    return 1;
  }

  if (!getValPriority(valA) && !getValPriority(valB)) {
    const dateFirst = moment(valA[0], 'MM/DD/YYYY');
    const dateSecond = moment(valB[0], 'MM/DD/YYYY');
    const dateIsSame = dateFirst.isSame(dateSecond) ? 0 : -1;

    return dateFirst.isAfter(dateSecond) ? 1 : dateIsSame;
  }
  return getValPriority(valA) - getValPriority(valB);
}

function getValPriority(val) {
  const dateRegexp = /(\d{2}\/\d{2}\/\d{4})/g;
  if (val.length > 1) {
    return firstCallDatePriorityRange.indexOf('Various');
  }
  if (val[0].match(dateRegexp)) {
    return firstCallDatePriorityRange.indexOf('date');
  }
  return firstCallDatePriorityRange.indexOf(val[0]);
}
